import { useRequestAssetAccess } from '@gripp/shared-logic';
import { Button, Colors, TextInput, tokens } from '@gripp/shared-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Modal, Portal, Text } from 'react-native-paper';

type RequestAccessModalProps = {
  visible: boolean;
  assetId: string;
  workspaceId: string;
  onClose: () => void;
  onSuccess: () => void;
};

/**
 * Web port of the mobile lonely-larry "Request full access" sheet: same
 * content and keys, presented as a centred Portal/Modal card (the
 * ShareIssuePanel precedent) instead of a bottom sheet.
 */
export const RequestAccessModal = ({
  visible,
  assetId,
  workspaceId,
  onClose,
  onSuccess,
}: RequestAccessModalProps) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { requestAssetAccess } = useRequestAssetAccess();

  const handleClose = () => {
    setMessage('');
    setHasError(false);
    onClose();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setHasError(false);
    try {
      await requestAssetAccess({
        assetId,
        workspaceId,
        message: message.trim() || undefined,
      });
      setMessage('');
      onSuccess();
      onClose();
    } catch {
      setHasError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={handleClose}
        contentContainerStyle={styles.modal}
      >
        <Text style={styles.title}>{t('asset.accessRequest.modalTitle')}</Text>
        <Text style={styles.subtitle}>
          {t('asset.accessRequest.modalSubtitle')}
        </Text>
        <View style={styles.input}>
          <TextInput
            textArea
            numberOfLines={5}
            placeholder={t('asset.accessRequest.modalPlaceholder')}
            value={message}
            onChangeText={setMessage}
            autoFocus
            accessibilityLabel={t('asset.accessRequest.modalPlaceholder')}
            testID="request-access-message"
          />
        </View>
        {hasError && (
          <Text style={styles.error}>{t('errorBoundary.message')}</Text>
        )}
        <View style={styles.footer}>
          <Button
            variant="rectangular"
            state="active"
            size="large"
            disabled={isSubmitting}
            onPress={handleSubmit}
            style={styles.actionButton}
            testID="request-access-submit"
          >
            {t('asset.accessRequest.modalSubmit')}
          </Button>
          <Button
            variant="rectangular"
            state="default"
            size="large"
            onPress={handleClose}
            style={styles.actionButton}
            testID="request-access-cancel"
          >
            {t('topNav.cancel')}
          </Button>
        </View>
      </Modal>
    </Portal>
  );
};

const styles = StyleSheet.create({
  modal: {
    backgroundColor: Colors.role.surface,
    marginHorizontal: 'auto',
    borderRadius: tokens.borderRadius.md,
    padding: tokens.spacing.xl,
    width: 420,
    maxWidth: '90%',
    gap: tokens.spacing.md,
  },
  title: {
    ...tokens.typography.title.small,
    color: Colors.role.textStrong,
  },
  subtitle: {
    ...tokens.typography.label.medium,
    color: Colors.role.textSecondary,
  },
  input: {
    marginTop: tokens.spacing.sm,
  },
  error: {
    ...tokens.typography.label.medium,
    color: Colors.danger,
  },
  footer: {
    gap: tokens.spacing.sm,
    marginTop: tokens.spacing.sm,
  },
  // Same full-width, 48-high primary action the settings/form screens use.
  actionButton: {
    width: '100%',
    height: 48,
    borderRadius: tokens.borderRadius.sm,
  },
});
